import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiTarget,
  FiEye,
  FiHeart,
  FiAward,
  FiUsers,
  FiBook,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiCamera,
} from "react-icons/fi";
import { settingsAPI } from "../services/api";
import useScrollAnimation from "../hooks/useScrollAnimation";
import {
  DEFAULT_SCHOOL_STATS,
  withStatDefaults,
  teacherStudentRatio,
} from "../utils/schoolStats";

const fallbackLeadership = [
  {
    name: "Dr. Ramesh Kumar Sharma",
    role: "Principal",
    description:
      "PhD in Education, 25+ years of experience in educational leadership.",
  },
  {
    name: "Mrs. Sunita Thapa",
    role: "Vice Principal",
    description:
      "Masters in Education Administration, dedicated to student welfare.",
  },
  {
    name: "Mr. Bikram Prasad",
    role: "Academic Director",
    description: "PhD in Curriculum Development, expert in modern pedagogy.",
  },
];

const fallbackFounderMembers = [
  { nameEn: "Lakshmikanta Paudel",    nameNp: "लक्ष्मीकान्त पौडेल" },
  { nameEn: "Tara Prasad Sharma",      nameNp: "तारा प्रसाद शर्मा" },
  { nameEn: "Bishnu Narayan Shrestha", nameNp: "बिष्णु नारायण श्रेष्ठ" },
  { nameEn: "Gir Bahadur Thapa",       nameNp: "गिर बहादुर थापा" },
  { nameEn: "Lekhnath Paudel",         nameNp: "लेखनाथ पौडेल" },
  { nameEn: "Liladhar Dhakal",         nameNp: "लिलाधर ढकाल" },
  { nameEn: "Liladhar Sharma",         nameNp: "लिलाधर शर्मा" },
  { nameEn: "Pharsuiram Panta",        nameNp: "फर्सुराम पन्त" },
  { nameEn: "Atmaaram Paudel",         nameNp: "आत्माराम पौडेल" },
  { nameEn: "Chak B. Thapa Magar",     nameNp: "चक ब. थापा मगर" },
  { nameEn: "Sur B. Fauja",            nameNp: "स्व.सुर ब. फौजा" },
  { nameEn: "Hemavati Thapa",          nameNp: "हेमवती थापा" },
  { nameEn: "Khumananda Acharya",      nameNp: "खुमानन्द आचार्य" },
  { nameEn: "Kaladhar Sharma",         nameNp: "कलाधर शर्मा" },
  { nameEn: "Bam B. Gurung",           nameNp: "बम ब. गुरुङ्ग" },
  { nameEn: "Numa Datta Adhikari",     nameNp: "नुमा दत्त अधिकारी" },
  { nameEn: "Nabaraj Khanal",          nameNp: "नवराज खनाल" },
  { nameEn: "Saraswati K.C.",          nameNp: "सरस्वती के.सी" },
  { nameEn: "Motilal Dhakal",          nameNp: "मोतीलाल ढकाल" },
  { nameEn: "Tulasa Ku. Ghimire",      nameNp: "तुलसा कु. घिमिरे" },
  { nameEn: "Thaneshwar Upadhyaya",    nameNp: "थानेश्वर उपाध्याय" },
  { nameEn: "Krishna Pra. Upadhyaya",  nameNp: "कृष्ण प्रा.उपाध्याय" },
  { nameEn: "Parvati Devi Parajuli",   nameNp: "पार्वती देवी पराजुली" },
  { nameEn: "Dinanath Acharya",        nameNp: "दिनानाथ आचार्य" },
  { nameEn: "Thaneshwar Acharya",      nameNp: "थानेश्वर आचार्य" },
  { nameEn: "Ayodhya Tharu",           nameNp: "आयोध्या थारु" },
  { nameEn: "Padma Paudel",            nameNp: "पदमा पौडेल" },
  { nameEn: "Ishwari Gautam",          nameNp: "इश्वरी गौतम" },
  { nameEn: "Gaura Thapa",             nameNp: "गौरा थापा" },
  { nameEn: "Kavita Paudel",           nameNp: "कविता पौडेल" },
  { nameEn: "Keshavraj Paudel",        nameNp: "केशवराज पौडेल" },
  { nameEn: "Lu Maya Gurung",          nameNp: "लु माया गुरुङ्ग" },
];

const fallbackAchievements = [
  {
    title: "ISA by British Council",
    desc: "Outstanding development of international dimension in the curriculum",
    photos: [],
  },
  {
    title: "District Education Office Award",
    desc: "Outstanding result in SEE and SLC examinations",
    photos: [],
  },
  {
    title: "Local Government Appreciation",
    desc: "Letter of appreciation for outstanding results in board exams",
    photos: [],
  },
  {
    title: "Consistently High Pass Rate",
    desc: "Consistently high SEE/SLC results every year",
    photos: [],
  },
];

const achievementIcons = [FiAward, FiUsers, FiBook, FiAward];

const getInitials = (name) =>
  name
    ? name
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

const About = () => {
  const [leadership, setLeadership] = useState(fallbackLeadership);
  const [coordinators, setCoordinators] = useState([]);
  const [staff, setStaff] = useState([]);
  const [founderMembers, setFounderMembers] = useState(fallbackFounderMembers);
  const [achievements, setAchievements] = useState(fallbackAchievements);
  const [achievementLightbox, setAchievementLightbox] = useState(null); // { title, photos, index }
  const [schoolStats, setSchoolStats] = useState(DEFAULT_SCHOOL_STATS);

  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation();
  const { ref: leaderRef, isVisible: leaderVisible } = useScrollAnimation();
  const { ref: coordRef, isVisible: coordVisible } = useScrollAnimation();
  const { ref: staffRef, isVisible: staffVisible } = useScrollAnimation();
  const { ref: achieveRef, isVisible: achieveVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const s = res.data?.data?.settings;
        if (s?.leadership?.length > 0) setLeadership(s.leadership);
        if (s?.coordinators?.length > 0) setCoordinators(s.coordinators);
        if (s?.staff?.length > 0) setStaff(s.staff);
        if (s?.founderMembers?.length > 0) setFounderMembers(s.founderMembers);
        if (s?.achievements?.length > 0) setAchievements(s.achievements);
        setSchoolStats(withStatDefaults(s?.schoolStats));
      })
      .catch(() => {});
  }, []);

  const openAchievementPhoto = (achievement, index) => {
    const photos = achievement.photos || [];
    if (photos.length === 0) return;
    setAchievementLightbox({ title: achievement.title, photos, index });
  };
  const closeAchievementLightbox = () => setAchievementLightbox(null);
  const prevAchievementPhoto = () =>
    setAchievementLightbox((l) => l && { ...l, index: (l.index - 1 + l.photos.length) % l.photos.length });
  const nextAchievementPhoto = () =>
    setAchievementLightbox((l) => l && { ...l, index: (l.index + 1) % l.photos.length });

  useEffect(() => {
    const handleKey = (e) => {
      if (!achievementLightbox) return;
      if (e.key === "ArrowLeft") prevAchievementPhoto();
      if (e.key === "ArrowRight") nextAchievementPhoto();
      if (e.key === "Escape") closeAchievementLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [achievementLightbox]);

  return (
    <div>
      {/* Hero Banner */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Know Us Better
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            About Us
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Discover our story, values, and the people who make Buddha Deep
            English Boarding Secondary School exceptional
          </p>
        </motion.div>
      </div>

      {/* School History */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="section-subtitle mb-2">Our History</p>
              <h2 className="section-title mb-6">
                A Journey of Excellence Since 2000
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Sited in a serene and tranquil environment, BDEBSS is a
                  premier and time-tested academic hub for top-notch quality
                  education and all-round development for students in Rupandehi,
                  Nepal. Since its inception in 2000 A.D, BDEBSS has carved out
                  a niche as the pinnacle of the best +2 school in Rupandehi
                  District.
                </p>
                <p>
                  BDEBSS has renovated and refurbished its facilities time and
                  again and upholds a techno-friendly and congenial academic
                  milieu conducive to teaching-learning activities. It boasts a
                  holistic approach enriching students' learning experience
                  through practical and student-centered teaching — modern,
                  resourceful, innovative and enterprising.
                </p>
                <p>
                  Today, with {schoolStats.students} students,{" "}
                  {schoolStats.teachers} teaching faculty and{" "}
                  {schoolStats.nonTeachingStaff} non-teaching staff, BDEBSS
                  stands as a testament to what
                  passionate educators and a supportive community can achieve,
                  guiding the avenues for the future of today's generation.
                </p>
              </div>
            </motion.div>

            {/* Timeline */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                {
                  year: "2000",
                  event: "BDEBSS founded in Kanchan Rural Municipality, Rupandehi by 40 founder members",
                  color: "bg-primary-500",
                },
                {
                  year: "2005",
                  event: "Expanded to Secondary School level with modern infrastructure",
                  color: "bg-secondary-500",
                },
                {
                  year: "2010",
                  event: "Added High School (+2) program — Management stream",
                  color: "bg-primary-500",
                },
                {
                  year: "2015",
                  event: "ISA award by British Council for international curriculum excellence",
                  color: "bg-secondary-500",
                },
                {
                  year: "2020",
                  event: "Launched AV-enabled smart classrooms and happiness classes",
                  color: "bg-primary-500",
                },
                {
                  year: "2024",
                  event: "District Education Office award for outstanding SEE/SLC results",
                  color: "bg-secondary-500",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div
                    className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
                  >
                    {item.year.slice(2)}
                  </div>
                  <div className="pt-2">
                    <p className="font-bold text-primary-500 text-sm">
                      {item.year}
                    </p>
                    <p className="text-gray-600 text-sm">{item.event}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={missionRef}
            initial={{ opacity: 0, y: 20 }}
            animate={missionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Foundation</p>
            <h2 className="section-title">Mission, Vision & Values</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FiTarget,
                title: "Our Mission",
                color: "bg-blue-50 border-blue-100 text-blue-500",
                desc: "Education for peace Creation and Human Development — instilling discipline, nurturing decency, and inculcating moral values with ardor for learning through edge-cut labs, ultra-modern technology, and industry-experienced faculty.",
              },
              {
                icon: FiEye,
                title: "Our Vision",
                color: "bg-primary-50 border-primary-100 text-primary-500",
                desc: "To unfold students' innate potential by encouraging initiative, futuristic outlook, and cultural consciousness — preparing highly qualified human resources needed for the global market of the 21st century.",
              },
              {
                icon: FiHeart,
                title: "Our Values",
                color:
                  "bg-secondary-50 border-secondary-100 text-secondary-600",
                desc: "Ethics, team spirit, and community service are strongly imbibed in our students. We cater holistic development — physical, mental, and emotional — through co-curricular and extra-curricular activities.",
              },
            ].map((card, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={missionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                className="bg-white border rounded-3xl p-8 text-center shadow-sm card-hover"
              >
                <div
                  className={`w-16 h-16 ${card.color} border rounded-2xl flex items-center justify-center mx-auto mb-5`}
                >
                  <card.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-primary-500 font-serif mb-4">
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* School at a Glance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-2">Quick Facts</p>
            <h2 className="section-title">School at a Glance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { label: "Established", value: "2000 A.D" },
              { label: "Nature", value: "Private Instruction" },
              { label: "Location", value: "Lumbini Province, Rupandehi District, Kanchan Rural Municipality, Ward No. 5" },
              { label: "Founder Members", value: "40" },
              { label: "Regulating Bodies", value: "Nepal Government, Ministry of Education, Science and Technology" },
              { label: "Classes", value: "Play Group to Class 12" },
              { label: "No. of Students", value: `${schoolStats.students}` },
              { label: "Extra-curricular Classes", value: "Happiness Classes, Dance & Music, Taekwondo" },
              { label: "Core Faculty", value: "Management (Finance, Computer, Business, Mathematics, Hotel Management)" },
              { label: "Teaching Faculty", value: `${schoolStats.teachers}` },
              { label: "Non-Teaching Faculty", value: `${schoolStats.nonTeachingStaff}` },
              { label: "Teacher–Student Ratio", value: teacherStudentRatio(schoolStats) },
              { label: "Medium of Teaching", value: "English" },
              { label: "Audio Visual Facilities", value: "Yes" },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-secondary-600 uppercase tracking-wide mb-0.5">{item.label}</p>
                  <p className="text-gray-800 text-sm font-medium">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Share Members */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-2">संस्थापक शेयर सदस्य</p>
            <h2 className="section-title">Founder Share Members</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {founderMembers.map((member, idx) => {
              const initials = getInitials(member.nameEn);
              const colors = [
                "bg-primary-500", "bg-secondary-500", "bg-blue-500",
                "bg-indigo-500", "bg-teal-500", "bg-purple-500",
              ];
              const bg = colors[idx % colors.length];
              return (
                <motion.div
                  key={member._id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: (idx % 12) * 0.05 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center text-center"
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden mb-3 shadow-md flex-shrink-0">
                    {member.photo?.url ? (
                      <img
                        src={member.photo.url}
                        alt={member.nameEn}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className={`w-full h-full rounded-full ${bg} flex items-center justify-center text-white text-lg font-bold`}>
                        {initials}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-800 text-xs font-semibold leading-snug mb-0.5">{member.nameNp}</p>
                  <p className="text-gray-400 text-xs leading-snug">{member.nameEn}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership — dynamic */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={leaderRef}
            initial={{ opacity: 0, y: 20 }}
            animate={leaderVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Leadership</p>
            <h2 className="section-title">Meet the Administration</h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8">
            {leadership.map((person, idx) => (
              <motion.div
                key={person._id || idx}
                initial={{ opacity: 0, y: 30 }}
                animate={leaderVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="w-full sm:w-[22rem] bg-gray-50 border border-gray-100 rounded-3xl p-8 text-center card-hover"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-5 flex-shrink-0 shadow-md">
                  {person.photo?.url ? (
                    <img
                      src={person.photo.url}
                      alt={person.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full bg-primary-500 flex items-center justify-center text-white text-3xl font-bold">
                      {getInitials(person.name)}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-primary-500 text-lg font-serif mb-1">
                  {person.name}
                </h3>
                <p className="text-secondary-600 text-sm font-medium mb-3">
                  {person.role}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {person.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coordinators — dynamic */}
      {coordinators.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={coordRef}
              initial={{ opacity: 0, y: 20 }}
              animate={coordVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-14"
            >
              <p className="section-subtitle mb-2">Academic Coordination</p>
              <h2 className="section-title">Our Coordinators</h2>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6">
              {coordinators.map((person, idx) => (
                <motion.div
                  key={person._id || idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={coordVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: (idx % 8) * 0.1 }}
                  className="w-full sm:w-[17rem] bg-white border border-gray-100 rounded-3xl p-6 text-center card-hover"
                >
                  <div className="w-28 h-28 rounded-full overflow-hidden mx-auto mb-4 flex-shrink-0 shadow-md">
                    {person.photo?.url ? (
                      <img
                        src={person.photo.url}
                        alt={person.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary-500 flex items-center justify-center text-white text-2xl font-bold">
                        {getInitials(person.name)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-primary-500 text-base font-serif mb-1">
                    {person.name}
                  </h3>
                  <p className="text-secondary-600 text-sm font-medium mb-1">
                    {person.post}
                  </p>
                  {person.qualification && (
                    <p className="text-gray-500 text-xs leading-relaxed">
                      {person.qualification}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Staff & Faculty — dynamic */}
      {staff.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={staffRef}
              initial={{ opacity: 0, y: 20 }}
              animate={staffVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-14"
            >
              <p className="section-subtitle mb-2">Our People</p>
              <h2 className="section-title">Staff & Faculty</h2>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {staff.map((person, idx) => (
                <motion.div
                  key={person._id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={staffVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: (idx % 12) * 0.05 }}
                  className="bg-gray-50 border border-gray-100 rounded-2xl p-5 text-center card-hover"
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 flex-shrink-0 shadow-md">
                    {person.photo?.url ? (
                      <img
                        src={person.photo.url}
                        alt={person.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary-500 flex items-center justify-center text-white text-xl font-bold">
                        {getInitials(person.name)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-primary-500 text-sm mb-1">
                    {person.name}
                  </h3>
                  <p className="text-secondary-600 text-xs font-medium mb-1">
                    {person.post}
                  </p>
                  {person.qualification && (
                    <p className="text-gray-500 text-xs leading-relaxed">
                      {person.qualification}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Achievements — dynamic */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={achieveRef}
            initial={{ opacity: 0, y: 20 }}
            animate={achieveVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Pride</p>
            <h2 className="section-title">Achievements & Recognition</h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {achievements.map((item, idx) => {
              const Icon = achievementIcons[idx % achievementIcons.length];
              const photos = item.photos || [];
              const cover = photos[0];
              return (
                <motion.div
                  key={item._id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={achieveVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: (idx % 12) * 0.05 }}
                  className="bg-white border border-gray-100 rounded-2xl p-5 text-center card-hover"
                >
                  {cover ? (
                    <button
                      type="button"
                      onClick={() => openAchievementPhoto(item, 0)}
                      className="relative block w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 flex-shrink-0 group"
                    >
                      <img
                        src={cover.url}
                        alt={cover.caption || item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {photos.length > 1 && (
                        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] font-bold py-0.5 flex items-center justify-center gap-0.5">
                          <FiCamera className="w-2.5 h-2.5" /> {photos.length}
                        </span>
                      )}
                    </button>
                  ) : (
                    <div className="w-20 h-20 bg-secondary-50 border border-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                      <Icon className="w-8 h-8 text-secondary-600" />
                    </div>
                  )}
                  <h4 className="font-bold text-primary-500 text-sm mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-500 text-xs leading-relaxed whitespace-pre-line">
                    {item.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievement photo lightbox */}
      <AnimatePresence>
        {achievementLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeAchievementLightbox}
          >
            <button
              onClick={closeAchievementLightbox}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiX className="w-6 h-6" />
            </button>
            {achievementLightbox.photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevAchievementPhoto(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronLeft className="w-7 h-7" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextAchievementPhoto(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronRight className="w-7 h-7" />
                </button>
              </>
            )}

            <motion.div
              key={achievementLightbox.index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img
                src={achievementLightbox.photos[achievementLightbox.index].url}
                alt={achievementLightbox.photos[achievementLightbox.index].caption || achievementLightbox.title}
                className="w-full max-h-[75vh] object-contain rounded-2xl"
              />
              <div className="text-center mt-4">
                {achievementLightbox.photos[achievementLightbox.index].caption && (
                  <h3 className="text-white font-semibold">
                    {achievementLightbox.photos[achievementLightbox.index].caption}
                  </h3>
                )}
                <p className="text-white/60 text-sm mt-1">{achievementLightbox.title}</p>
              </div>
              {achievementLightbox.photos.length > 1 && (
                <div className="text-center mt-3 text-white/40 text-sm">
                  {achievementLightbox.index + 1} / {achievementLightbox.photos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default About;
